import { deleteJob, getJobs, searchJobs } from '@assembly/api/jobs'
import { ConfirmDeleteDialog, SearchInputAndButton } from '@assembly/components'
import { SnackbarType } from '@assembly/contexts'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { getApiErrorMsg } from '@assembly/utils'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Pagination,
  Typography,
} from '@mui/material'
import React from 'react'
import JobsList from './JobsList'
import CreateJobDialog from './JobCreateDialog'
import { Job, JobStatus } from './types'
import { capitalize, debounce } from 'lodash'

const FiltersBox = styled(Box)(({ theme }) => ({
  marginTop: '15px',
  width: '50%',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}))

export default function Jobs() {
  const { addAlert } = useSnackbars()
  const { store, setJobs } = useAppStore()
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [createJobDialogOpen, setCreateJobDialogOpen] =
    React.useState<boolean>(false)
  const [selectedJob, setSelectedJob] = React.useState<{
    job: Job | undefined
    index: number
  }>({
    job: undefined,
    index: -1,
  })
  const [confirmDeleteDialogIsVisible, setConfirmDeleteDialogVisible] =
    React.useState<boolean>(false)
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false)
  const [deleteInputValue, setDeleteInputValue] = React.useState<string>('')
  const [searchResults, setSearchResults] = React.useState<Job[]>()
  const [status, setSatatus] = React.useState<string>(JobStatus.OPEN)
  const [isSeachLoading, setIsSearchLoading] = React.useState<boolean>(false)
  const [searchValue, setSearchValue] = React.useState<string>('')
  const [currentPage, setCurrentPage] = React.useState<number>(0)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [totalPages, setTotalPages] = React.useState<number>(0)
  const [totalItems, setTotalItems] = React.useState<number>(0)
  const dataLoadedRef = React.useRef(false)

  React.useEffect(() => {
    if (!dataLoadedRef.current) {
      dataLoadedRef.current = true
      getJobsAsync()
    }
  }, [])

  const handleChangeSearch = debounce((value) => {
    setSearchValue(value)
    setCurrentPage(0) // Reset to first page when searching
    searchJobsAsync(value, status, 0)
  }, 1000)

  const handleCreate = () => {
    setCreateJobDialogOpen(true)
  }

  const getJobsAsync = async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams()
      params.append('search', '')
      params.append('status', JobStatus.OPEN)
      const { data } = await searchJobs(params, currentPage, pageSize)
      console.log('Initial API Response:', data) // Debug log
      setJobs(data.data || [])
      setTotalPages(data.totalPages || 0)
      setTotalItems(data.totalCount || 0)
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  const handleOnCreateSuccess = (job: Job) => {
    setJobs([...store.jobs, job])
  }

  const handleOnEditSuccess = (job: Job) => {
    const newJobs = [...store.jobs]
    newJobs[selectedJob.index] = job
    setJobs(newJobs)
  }

  const handleClickEdit = (job: Job, index: number) => {
    setSelectedJob({ job, index })
    setCreateJobDialogOpen(true)
  }

  const handleCloseConfirmDeleteDialog = () => {
    setConfirmDeleteDialogVisible(false)
    setSelectedJob({ job: undefined, index: -1 })
  }

  const handleChangeDeleteInputValue = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDeleteInputValue(e.target.value)
  }

  const removeFromList = () => {
    if (selectedJob.index === -1) {
      return
    }

    const newJobs = [...store.jobs]
    newJobs.splice(selectedJob.index, 1)
    setJobs(newJobs)
  }

  const deleteAsync = async () => {
    if (!selectedJob.job) {
      return
    }

    setIsDeleting(true)

    try {
      await deleteJob(selectedJob.job.id)
      setIsDeleting(false)
      removeFromList()
      setSelectedJob({ job: undefined, index: -1 })
      setDeleteInputValue('')
      addAlert({
        message: 'Job Deleted!',
        type: SnackbarType.Success,
      })
      setConfirmDeleteDialogVisible(false)
    } catch (error) {
      setIsDeleting(false)
      addAlert({ message: 'Error', type: SnackbarType.Error })
    }
  }

  const handleClickDelete = (job: Job, index: number) => {
    setSelectedJob({ job, index })
    setConfirmDeleteDialogVisible(true)
  }

  const handeClose = () => {
    setCreateJobDialogOpen(false)
    setSelectedJob({ job: undefined, index: -1 })
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const { value } = event.target
    setSatatus(value)
    setCurrentPage(0) // Reset to first page when changing status
    searchJobsAsync(searchValue, value, 0)
  }

  const searchJobsAsync = async (search: string, status: string, page: number = currentPage) => {
    try {
      setIsSearchLoading(true)
      const params = new URLSearchParams()
      params.append('search', search || '')
      if (status) {
        params.append('status', status || '')
      }

      const { data } = await searchJobs(params, page, pageSize)
      console.log('API Response:', data) // Debug log
      setJobs(data.data || [])
      setTotalPages(data.totalPages || 0)
      setTotalItems(data.totalCount || 0)
      setCurrentPage(page)
      setIsSearchLoading(false)
    } catch (error) {
      setIsSearchLoading(false)
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    }
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    const pageIndex = page - 1 // Convert to 0-indexed
    setCurrentPage(pageIndex)
    searchJobsAsync(searchValue, status, pageIndex)
  }

  return (
    <Box>
      <SearchInputAndButton
        inputPlaceholder="Search Jobs"
        onChangeSearch={handleChangeSearch}
        buttonLabel="Create Job"
        onClickButton={handleCreate}
      />
      <FiltersBox>
        <FormControl fullWidth size="small">
          <InputLabel id="status-select-label">Search By Status</InputLabel>
          <Select
            labelId="status-select-label"
            value={status}
            label={'Search by status'}
            name="status"
            onChange={handleChangeSelect}
            placeholder="Search by status"
          >
            {Object.values(JobStatus).map((value) => (
              <MenuItem key={value} value={value}>
                {capitalize(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FiltersBox>
      {isSeachLoading ? (
        <Box display="flex" justifyContent="center" marginTop={2}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <JobsList
            jobs={Array.isArray(searchResults || store.jobs) ? (searchResults || store.jobs) : []}
            onClickDelete={handleClickDelete}
            onClickEdit={handleClickEdit}
          />
          {console.log('Pagination Debug:', { totalPages, totalItems, currentPage, pageSize })}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop={3} gap={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalItems)} of {totalItems} jobs
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage + 1} // Convert to 1-indexed for MUI Pagination
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
      <CreateJobDialog
        open={createJobDialogOpen}
        onClose={handeClose}
        job={selectedJob.job}
        onSuccess={
          selectedJob.job ? handleOnEditSuccess : handleOnCreateSuccess
        }
      />
      <ConfirmDeleteDialog
        open={confirmDeleteDialogIsVisible}
        onClose={handleCloseConfirmDeleteDialog}
        name={selectedJob.job?.title || ''}
        onClickDelete={deleteAsync}
        isLoading={isDeleting}
        inputValue={deleteInputValue}
        onChangeInput={handleChangeDeleteInputValue}
      />
    </Box>
  )
}
